/**
 * Falling-egg rules: which bonus a crack grants, how boosts stack (they
 * don't), and when the next egg spawns. Pure; randomness is injected as a
 * function so the tests can pin it.
 */

import { BONUSES, SPAWN_MAX_SECONDS, SPAWN_MIN_SECONDS } from '../data/bonuses';
import type { BonusDef } from '../data/bonuses';
import { earnEggs } from './state';
import { eraModifier, skyHaste } from './rebirth';
import type { GameState } from './state';

// ---------------------------------------------------------------------------
// Picking

/** Weighted pick. `roll` is in [0, 1); each bonus owns a slice sized by its weight. */
export function pickBonus(roll: number, bonuses: readonly BonusDef[] = BONUSES): BonusDef {
  const total = bonuses.reduce((sum, b) => sum + b.weight, 0);
  let cursor = Math.min(Math.max(roll, 0), 1 - Number.EPSILON) * total;
  for (const b of bonuses) {
    if (cursor < b.weight) return b;
    cursor -= b.weight;
  }
  return bonuses[bonuses.length - 1]!;
}

// ---------------------------------------------------------------------------
// Boosts

/** Drops boosts whose time is up. */
export function expireBoost(state: GameState, now: number): void {
  if (state.boost && state.boost.endsAt <= now) state.boost = null;
  if (state.clickBoost && state.clickBoost.endsAt <= now) state.clickBoost = null;
}

/** The production multiplier from the active boost, or 1. */
export function activeBoostMultiplier(state: GameState, now: number): number {
  return state.boost && state.boost.endsAt > now ? state.boost.mult : 1;
}

/** The per-click multiplier from the active click boost, or 1. */
export function activeClickMultiplier(state: GameState, now: number): number {
  return state.clickBoost && state.clickBoost.endsAt > now ? state.clickBoost.mult : 1;
}

export function boostSecondsLeft(state: GameState, now: number): number {
  return state.boost ? Math.max(0, (state.boost.endsAt - now) / 1000) : 0;
}

export function clickBoostSecondsLeft(state: GameState, now: number): number {
  return state.clickBoost ? Math.max(0, (state.clickBoost.endsAt - now) / 1000) : 0;
}

// ---------------------------------------------------------------------------
// Applying a caught egg

export type CrackResult =
  | { kind: 'boost'; bonus: BonusDef; seconds: number }
  | { kind: 'clickBoost'; bonus: BonusDef; seconds: number }
  | { kind: 'jackpot'; bonus: BonusDef; eggs: number }
  /** A weaker boost caught while a stronger one is running does nothing. */
  | { kind: 'whiff'; bonus: BonusDef };

/**
 * Grants a bonus to the state. `eps` is the current effective rate (boosted,
 * since that is what the player sees) for the jackpot calculation.
 */
export function applyBonus(
  bonus: BonusDef,
  state: GameState,
  eps: number,
  now: number,
): CrackResult {
  state.cracks += 1;
  state.bonusesCaught[bonus.id] = (state.bonusesCaught[bonus.id] ?? 0) + 1;
  const e = bonus.effect;

  if (e.kind === 'jackpot') {
    const span = eps * e.minutesOfEps * 60;
    const cap = state.eggs * e.capFractionOfBank;
    const eggs = Math.max(e.floor, Math.min(span, cap));
    earnEggs(state, eggs);
    return { kind: 'jackpot', bonus, eggs };
  }

  // Boosts don't stack within a slot. A stronger or equal one replaces (and
  // refreshes) the current one; a weaker one is a whiff. The production and
  // click slots are independent, so one of each can run at once.
  if (e.kind === 'clickBoost') {
    if (e.mult < activeClickMultiplier(state, now)) return { kind: 'whiff', bonus };
    state.clickBoost = { kind: e.boost, mult: e.mult, endsAt: now + e.seconds * 1000 };
    return { kind: 'clickBoost', bonus, seconds: e.seconds };
  }
  if (e.mult < activeBoostMultiplier(state, now)) return { kind: 'whiff', bonus };
  state.boost = { kind: e.boost, mult: e.mult, endsAt: now + e.seconds * 1000 };
  return { kind: 'boost', bonus, seconds: e.seconds };
}

// ---------------------------------------------------------------------------
// Spawn timer

export interface SpawnTimer {
  /** Seconds of active play until the next spawn. */
  readonly remaining: number;
  /** Advance by `seconds` of active play. True when an egg should spawn now. */
  advance(seconds: number): boolean;
  /** Start a fresh random interval. Called after every spawn, caught or not. */
  reset(): void;
  /**
   * Change the interval the next reset draws from. An era modifier can make
   * things fall far more often, and the clock has to follow without being
   * rebuilt underneath a run.
   */
  setRange(min: number, max: number): void;
}

/**
 * Seconds between golden eggs under this era, or null for an era with none.
 */
export function goldenGap(
  state: GameState,
  now: number = Date.now(),
): { min: number; max: number } | null {
  const haste = skyHaste(state, now);
  const era = eraModifier(state);
  if (era && 'goldenGap' in era) {
    if (era.goldenGap === null) return null;
    if (era.goldenGap !== undefined) {
      return { min: era.goldenGap / haste, max: era.goldenGap / haste };
    }
  }
  return { min: SPAWN_MIN_SECONDS / haste, max: SPAWN_MAX_SECONDS / haste };
}

export function createSpawnTimer(
  random: () => number = Math.random,
  min: number = SPAWN_MIN_SECONDS,
  max: number = SPAWN_MAX_SECONDS,
): SpawnTimer {
  let remaining = 0;
  let lo = min;
  let hi = max;
  const timer: SpawnTimer = {
    get remaining() {
      return remaining;
    },
    advance(seconds) {
      remaining -= seconds;
      if (remaining > 0) return false;
      timer.reset();
      return true;
    },
    reset() {
      remaining = lo + random() * (hi - lo);
    },
    setRange(nextMin, nextMax) {
      if (nextMin === lo && nextMax === hi) return;
      lo = nextMin;
      hi = nextMax;
      // Pull an interval already longer than the new ceiling back in, so a
      // change takes effect now rather than after one more old-length wait.
      if (remaining > hi) remaining = hi;
    },
  };
  timer.reset();
  return timer;
}
